import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import useThemeStyles from '../../../hooks/useThemeStyles';

const ChapterDetectionStep = ({
  usePageBreaks,
  onUsePageBreaksChange,
  skipTitlePage,
  onSkipTitlePageChange,
  chapterPattern,
  onChapterPatternChange,
}) => {
  const { colors } = useThemeStyles();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" sx={{ color: colors.text.secondary, textAlign: 'center' }}>
        Configure how chapters should be detected in your document.
      </Typography>

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={usePageBreaks}
              onChange={(e) => onUsePageBreaksChange(e.target.checked)}
            />
          }
          label="Use page breaks and common chapter patterns (Chapter 1, Part I, etc.)"
        />
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={skipTitlePage}
              onChange={(e) => onSkipTitlePageChange(e.target.checked)}
            />
          }
          label="Skip title page (first page)"
        />
      </Box>

      <Divider>
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          OR
        </Typography>
      </Divider>

      <Box>
        <Typography variant="body2" sx={{ mb: 1, color: colors.text.primary }}>
          Custom chapter pattern (regex or text):
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g., Chapter \d+, or --- for scene breaks"
          value={chapterPattern}
          onChange={(e) => onChapterPatternChange(e.target.value)}
          helperText="Leave empty to use default detection. Enter a pattern to split chapters."
          sx={{
            '& .MuiInputLabel-root': { color: colors.text.secondary },
            '& .MuiInputLabel-root.Mui-focused': { color: colors.interactive.primary },
            input: { color: colors.text.primary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: colors.border.subtle },
              '&:hover fieldset': { borderColor: colors.interactive.primary },
              '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
            },
          }}
        />
      </Box>

      <Alert severity="info" sx={{ mt: 1 }}>
        If no chapter markers are detected, the entire document will be imported as a single
        chapter.
      </Alert>
    </Box>
  );
};

ChapterDetectionStep.propTypes = {
  usePageBreaks: PropTypes.bool.isRequired,
  onUsePageBreaksChange: PropTypes.func.isRequired,
  skipTitlePage: PropTypes.bool.isRequired,
  onSkipTitlePageChange: PropTypes.func.isRequired,
  chapterPattern: PropTypes.string.isRequired,
  onChapterPatternChange: PropTypes.func.isRequired,
};

export default ChapterDetectionStep;
